import type { ChatMessage, FroshBotEngine, TurnEvent } from "./types";
import { makeMessage } from "./types";

/**
 * Port of `MockFroshBotService.swift` — the offline fallback, selected with
 * `?froshbot=mock`. Keyword-routed canned replies; navigate offers surface as
 * the same action chips the live path renders.
 */

const delay = (seconds: number) => new Promise<void>((r) => setTimeout(r, seconds * 1000));

interface CannedReply {
  text: string;
  tool?: { name: string; params: Record<string, string> };
}

function reply(content: string): CannedReply {
  return { text: content };
}

function route(lowered: string): CannedReply[] {
  if (lowered.includes("grade") || lowered.includes("gpa")) {
    return [
      reply("Let me check your GPA!"),
      { text: "", tool: { name: "getGPA", params: {} } },
      {
        text: "",
        tool: { name: "navigate", params: { destination: "openStudy", label: "Go to Study" } },
      },
    ];
  }
  if (lowered.includes("course") || lowered.includes("class")) {
    return [
      reply("Let me pull up your courses!"),
      { text: "", tool: { name: "getCourses", params: {} } },
    ];
  }
  if (lowered.includes("due") || lowered.includes("assignment")) {
    return [
      reply("Here's what's coming up."),
      { text: "", tool: { name: "getAssignments", params: {} } },
    ];
  }
  if (lowered.includes("study") || lowered.includes("help")) {
    return [
      reply(
        "I'd love to help you study! Which course do you want to focus on — CS 101, ENG 102, or MATH 1B?",
      ),
    ];
  }
  if (lowered.includes("match") || lowered.includes("vibe") || lowered.includes("roommate")) {
    return [
      reply("Let me check your matches!"),
      { text: "", tool: { name: "getMatches", params: {} } },
      {
        text: "",
        tool: {
          name: "navigate",
          params: { destination: "openMatchesFeed", label: "See Matches" },
        },
      },
    ];
  }
  if (lowered.includes("challenge")) {
    return [
      reply("Let me grab today's challenge!"),
      { text: "", tool: { name: "getTodayChallenge", params: {} } },
      {
        text: "",
        tool: { name: "navigate", params: { destination: "openCompete", label: "Go to Compete" } },
      },
    ];
  }
  if (lowered.includes("point") || lowered.includes("balance") || lowered.includes("reward")) {
    return [
      reply("Let me check your Frosh Points!"),
      { text: "", tool: { name: "getBalance", params: {} } },
    ];
  }
  return [
    reply(
      "Got it! I'm here to help with anything — classes, social life, content creation, or just chatting. What's on your mind?",
    ),
  ];
}

export class MockFroshBot implements FroshBotEngine {
  readonly live = false;
  readonly history: ChatMessage[] = [];

  async sendTurn(
    content: string,
    opts: { hidden?: boolean },
    onEvent: (event: TurnEvent) => void,
  ): Promise<void> {
    this.history.push(makeMessage({ role: "user", content, hidden: opts.hidden }));
    await delay(1.0);

    let full = "";
    for (const part of route(content.toLowerCase())) {
      if (part.text) {
        onEvent({ kind: "text", text: part.text });
        full += part.text;
      }
      if (part.tool) {
        onEvent({ kind: "tool", name: part.tool.name, params: part.tool.params });
      }
    }
    if (full.length > 0) {
      this.history.push(makeMessage({ role: "assistant", content: full }));
    }
  }
}
